// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import type { PubmedPaperSummary, PubmedPaperDetails } from '../../../../../services/pubmedService';
import type { EvidenceItem, AbstractTriageBrief } from '../../../lib/models';
import { categorizePapers } from '../types/resultCategorization';
import { useCategoryState } from '../hooks/useCategoryState';
import { PaperListItem } from './PaperListItem';
import { LibraryCategorizedResults } from './LibraryCategorizedResults';

export interface LibraryTabProps {
  libraryItems: EvidenceItem[];
  getCachedDetails: (pmid: string) => PubmedPaperDetails | undefined;
  onPaperClick: (pmid: string, cachedDetails?: PubmedPaperDetails) => void;
  onRemove: (pmid: string) => void;
  selectedInputSource: string | null;
  // AI Triage Props
  triageBriefs: Record<string, AbstractTriageBrief>;
  triageErrors: Record<string, { code: string; message: string }>;
  triageStatus: 'idle' | 'loading' | 'cancelled' | 'done' | 'error';
  failedPmids: string[];
  onBatchTriage: (pmids?: string[]) => Promise<void>;
  onRetryFailed: () => void;
}

export const LibraryTab: React.FC<LibraryTabProps> = ({
  libraryItems,
  getCachedDetails,
  onPaperClick,
  onRemove,
  selectedInputSource,
  triageBriefs,
  triageErrors,
  triageStatus,
  failedPmids,
  onBatchTriage,
  onRetryFailed,
}) => {
  // Always call hooks at the top level
  const { expandedCategories, activeFilter, toggleCategory, setFilter } = useCategoryState();

  if (libraryItems.length === 0) {
    return (
      <div className="evidence-drawer__empty">
        No saved papers — search PubMed to add evidence to your library
      </div>
    );
  }

  const analyzedCount = Object.keys(triageBriefs).length;
  // Calculate distinct error count (excluding those that succeeded in a retry)
  const distinctErrorPmids = Object.keys(triageErrors).filter(pmid => !triageBriefs[pmid]);
  const errorCount = distinctErrorPmids.length;

  // Convert library items to paper summaries for categorization
  const paperSummaries: PubmedPaperSummary[] = libraryItems.map(item => {
    const cachedDetails = getCachedDetails(item.id);
    return cachedDetails ? {
      pmid: cachedDetails.pmid,
      title: cachedDetails.title,
      journal: cachedDetails.journal,
      year: cachedDetails.year,
      firstAuthor: cachedDetails.authors?.[0] || item.firstAuthor,
      doi: cachedDetails.doi,
      url: cachedDetails.url,
    } : {
      pmid: item.id,
      title: item.title,
      journal: item.journal,
      year: item.year,
      firstAuthor: item.firstAuthor,
      doi: item.doi,
      url: item.url,
    };
  });

  // NEW: Categorize results for display
  const categorizedResults = categorizePapers(paperSummaries, triageBriefs, triageErrors);
  const showCategorizedView = analyzedCount > 0 || errorCount > 0;

  // Custom paper click handler that includes remove functionality
  const handlePaperClick = (pmid: string) => {
    const cachedDetails = getCachedDetails(pmid);
    onPaperClick(pmid, cachedDetails);
  };

  return (
    <div className="evidence-drawer__library-container">
      <div className="evidence-drawer__batch-controls">
        <button 
          className={`evidence-drawer__batch-button ${triageStatus === 'loading' ? 'evidence-drawer__batch-button--loading' : ''}`}
          onClick={() => onBatchTriage()}
          disabled={triageStatus === 'loading'}
        >
          {triageStatus === 'loading' ? (
            <>
              <div className="evidence-drawer__batch-spinner" />
              Analyzing {libraryItems.length} papers...
            </>
          ) : (
            `Analyze ${libraryItems.length} papers with AI`
          )}
        </button>

        {failedPmids.length > 0 && triageStatus === 'done' && (
          <button 
            className="evidence-drawer__batch-button evidence-drawer__batch-button--retry"
            onClick={onRetryFailed}
          >
            Retry Failed ({failedPmids.length} papers)
          </button>
        )}

        {(analyzedCount > 0 || errorCount > 0) && (
          <div className={`evidence-drawer__triage-summary ${errorCount > 0 ? 'evidence-drawer__triage-summary--partial' : 'evidence-drawer__triage-summary--success'}`}>
            ✓ {analyzedCount} analyzed {errorCount > 0 && `• ⚠ ${errorCount} failed`}
          </div>
        )}

        {triageStatus === 'error' && (
          <div className="evidence-drawer__error evidence-drawer__batch-error">
            Batch triage failed. Please try again.
          </div>
        )}
      </div>

      <div className="evidence-drawer__results">
        {/* Show categorized results if we have analyzed papers, otherwise show regular list */}
        {showCategorizedView ? (
          <LibraryCategorizedResults
            categorizedResults={categorizedResults}
            onPaperClick={handlePaperClick}
            onRemove={onRemove}
            selectedInputSource={selectedInputSource}
            activeFilter={activeFilter}
            onFilterChange={setFilter}
            expandedCategories={expandedCategories}
            onCategoryToggle={toggleCategory}
          />
        ) : (
          <>
            {libraryItems.map((item) => {
              // If we have a filter active, check if this paper matches
              const isHiddenByFilter = selectedInputSource && 
                (!item.aiBrief || !item.aiBrief.potentialInputSources.includes(selectedInputSource as any));

              if (isHiddenByFilter) return null;

              // Check cache first, otherwise will need to fetch
              const cachedDetails = getCachedDetails(item.id);
              const paperSummary: PubmedPaperSummary = cachedDetails ? {
                pmid: cachedDetails.pmid,
                title: cachedDetails.title,
                journal: cachedDetails.journal,
                year: cachedDetails.year,
                firstAuthor: cachedDetails.authors?.[0] || item.firstAuthor,
                doi: cachedDetails.doi,
                url: cachedDetails.url,
              } : {
                pmid: item.id,
                title: item.title,
                journal: item.journal,
                year: item.year,
                firstAuthor: item.firstAuthor,
                doi: item.doi,
                url: item.url,
              };
              
              return (
                <div key={item.id} className="evidence-drawer__library-item">
                  <PaperListItem
                    paper={paperSummary}
                    onClick={() => onPaperClick(item.id, cachedDetails)}
                    triageBrief={item.aiBrief}
                    highlightInputSource={selectedInputSource}
                  />
                  <button
                    className="evidence-drawer__remove-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    aria-label={`Remove ${item.title}`}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
